"use client"

import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ExecutionStatus } from "@/types/workflow"

export type Execution = {
  execution_id: string
  workflow_id: string
  status: ExecutionStatus
  steps_completed: number
  steps_total: number
  started_at?: string
  completed_at?: string
  duration?: number
}

export const columns: ColumnDef<Execution>[] = [
  {
    accessorKey: "workflow_id",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Workflow
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("workflow_id")}</div>
    ),
  },
  {
    accessorKey: "execution_id",
    header: "Execution ID",
    cell: ({ row }) => (
      <div className="font-mono text-sm">{row.getValue("execution_id")}</div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as ExecutionStatus
      const getStatusVariant = (status: ExecutionStatus) => {
        switch (status) {
          case ExecutionStatus.COMPLETED:
            return "default"
          case ExecutionStatus.RUNNING:
            return "secondary"
          case ExecutionStatus.FAILED:
            return "destructive"
          case ExecutionStatus.PENDING:
            return "outline"
          case ExecutionStatus.SKIPPED:
            return "outline"
          default:
            return "outline"
        }
      }

      const getStatusDisplay = (status: ExecutionStatus) => {
        switch (status) {
          case ExecutionStatus.COMPLETED:
            return "Completed"
          case ExecutionStatus.RUNNING:
            return "Running"
          case ExecutionStatus.FAILED:
            return "Failed"
          case ExecutionStatus.PENDING:
            return "Pending"
          case ExecutionStatus.SKIPPED:
            return "Skipped"
          default:
            return status
        }
      }

      return (
        <Badge variant={getStatusVariant(status)}>
          {getStatusDisplay(status)}
        </Badge>
      )
    },
  },
  {
    accessorKey: "progress",
    header: "Progress",
    cell: ({ row }) => {
      const stepsCompleted = row.original.steps_completed
      const stepsTotal = row.original.steps_total
      const progress = (stepsCompleted / stepsTotal) * 100

      return (
        <div className="flex items-center space-x-2">
          <Progress value={progress} className="w-20" />
          <span className="text-sm text-muted-foreground">
            {stepsCompleted}/{stepsTotal}
          </span>
        </div>
      )
    },
  },
  {
    accessorKey: "started_at",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Started
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const startedAt = row.getValue("started_at") as string | undefined
      if (!startedAt) return <span className="text-muted-foreground">N/A</span>
      
      const date = new Date(startedAt)
      return (
        <div className="text-sm">
          {date.toLocaleDateString()}
          <div className="text-xs text-muted-foreground">
            {date.toLocaleTimeString()}
          </div>
        </div>
      )
    },
  },
  {
    accessorKey: "duration",
    header: "Duration",
    cell: ({ row }) => {
      const duration = row.getValue("duration") as number | undefined
      if (!duration) return <span className="text-muted-foreground">N/A</span>
      
      const minutes = Math.floor(duration / 60)
      const seconds = duration % 60
      return (
        <span className="text-sm">
          {minutes > 0 ? `${minutes}m ` : ""}{seconds}s
        </span>
      )
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const execution = row.original

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(execution.execution_id)}
            >
              Copy execution ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>View details</DropdownMenuItem>
            <DropdownMenuItem>Download logs</DropdownMenuItem>
            <DropdownMenuItem>Re-run workflow</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  },
]